import {
  RESEND_COOLDOWN_AFTER_FIVE_ATTEMPTS_SEC,
  RESEND_COOLDOWN_NORMAL_SEC,
  TWO_FACTOR_RESEND_MAX_ATTEMPTS,
  TWO_FACTOR_VERIFY_ERRORS,
  TWO_FACTOR_VERIFY_LOCKOUT_DEFAULT_SEC,
  TWO_FACTOR_VERIFY_MAX_ATTEMPTS,
} from '../constants/twoFactorMessages';
import {
  readPersistedJson,
  removePersistedKey,
  writePersistedJson,
} from './twoFactorPersistedStorage';

const PREFIX = 'mhd:2fa-verify:';

const attemptsKey = (userId, sessionToken) =>
  `${PREFIX}attempts:${userId}:${sessionToken}`;

const fiveFlagKey = userId => `${PREFIX}five-flag:${userId}`;

const lockoutKey = userId => `${PREFIX}lockout:${userId}`;

const verifyLimitKey = userId => `${PREFIX}verify-limit:${userId}`;

const resendCooldownKey = userId => `${PREFIX}resend:${userId}`;

const resendCountKey = userId => `${PREFIX}resend-count:${userId}`;

const resendBlockedKey = userId => `${PREFIX}resend-blocked:${userId}`;

const resendGuidanceDismissedKey = userId =>
  `${PREFIX}resend-guidance-dismissed:${userId}`;

const readSessionJson = key => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const writeSessionJson = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // sessionStorage may be unavailable
  }
};

const removeSessionKey = key => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    // sessionStorage may be unavailable
  }
};

export const getFailedAttemptCount = (userId, sessionToken) => {
  if (!userId || !sessionToken) {
    return 0;
  }

  const parsed = readSessionJson(attemptsKey(userId, sessionToken));
  return parsed && parsed.count ? parsed.count : 0;
};

export const incrementFailedAttempt = (userId, sessionToken) => {
  if (!userId || !sessionToken) {
    return {count: 0, hitFiveAttemptFlag: false, lockout: null};
  }

  const count = getFailedAttemptCount(userId, sessionToken) + 1;
  writeSessionJson(attemptsKey(userId, sessionToken), {count});

  let hitFiveAttemptFlag = false;
  let lockout = null;
  if (count >= TWO_FACTOR_VERIFY_MAX_ATTEMPTS) {
    hitFiveAttemptFlag = true;
    writePersistedJson(fiveFlagKey(userId), {active: true});
    setVerifyAttemptLimitActive(userId);
    saveVerifyLockout(
      userId,
      TWO_FACTOR_VERIFY_LOCKOUT_DEFAULT_SEC,
      TWO_FACTOR_VERIFY_ERRORS.LOCKOUT,
    );
    lockout = getVerifyLockout(userId);
  }

  return {count, hitFiveAttemptFlag, lockout};
};

export const getHitFiveAttemptFlag = userId => {
  if (!userId) {
    return false;
  }

  const parsed = readPersistedJson(fiveFlagKey(userId));
  return Boolean(parsed && parsed.active);
};

export const clearHitFiveAttemptFlag = userId => {
  if (!userId) {
    return;
  }

  removePersistedKey(fiveFlagKey(userId));
};

export const setVerifyAttemptLimitActive = userId => {
  if (!userId) {
    return;
  }

  writePersistedJson(verifyLimitKey(userId), {active: true});
};

export const isVerifyAttemptLimitActive = userId => {
  if (!userId) {
    return false;
  }

  const parsed = readPersistedJson(verifyLimitKey(userId));
  return Boolean(parsed && parsed.active);
};

export const clearVerifyAttemptLimitActive = userId => {
  if (!userId) {
    return;
  }

  removePersistedKey(verifyLimitKey(userId));
};

export const resetVerifyAttempts = (userId, sessionToken) => {
  if (!userId) {
    return;
  }

  if (sessionToken) {
    removeSessionKey(attemptsKey(userId, sessionToken));
  }
};

export const resetVerifyStateAfterResend = (
  userId,
  oldSessionToken,
  newSessionToken,
) => {
  if (!userId) {
    return;
  }

  resetVerifyAttempts(userId, oldSessionToken);
  resetVerifyAttempts(userId, newSessionToken);
  clearVerifyLockout(userId);
  clearVerifyAttemptLimitActive(userId);
};

export const clearUserVerifyAttemptCounters = userId => {
  if (!userId) {
    return;
  }

  clearHitFiveAttemptFlag(userId);
  clearVerifyAttemptLimitActive(userId);

  try {
    const prefix = `${PREFIX}attempts:${userId}:`;
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith(prefix)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    // sessionStorage may be unavailable
  }
};

export const getResendCount = userId => {
  if (!userId) {
    return 0;
  }

  const parsed = readPersistedJson(resendCountKey(userId));
  return parsed && parsed.count ? parsed.count : 0;
};

export const incrementResendCount = userId => {
  if (!userId) {
    return 0;
  }

  const count = getResendCount(userId) + 1;
  writePersistedJson(resendCountKey(userId), {count});
  return count;
};

export const setResendCountToMax = userId => {
  if (!userId) {
    return;
  }

  writePersistedJson(resendCountKey(userId), {count: TWO_FACTOR_RESEND_MAX_ATTEMPTS});
};

export const isResendBlocked = userId => {
  if (!userId) {
    return false;
  }

  const parsed = readPersistedJson(resendBlockedKey(userId));
  return Boolean(parsed && parsed.blocked);
};

export const setResendBlocked = userId => {
  if (!userId) {
    return;
  }

  writePersistedJson(resendBlockedKey(userId), {blocked: true});
};

export const clearResendTracking = userId => {
  if (!userId) {
    return;
  }

  removePersistedKey(resendCountKey(userId));
  removePersistedKey(resendBlockedKey(userId));
};

export const hasResendGuidanceBeenDismissed = userId => {
  if (!userId) {
    return false;
  }

  const parsed = readPersistedJson(resendGuidanceDismissedKey(userId));
  return Boolean(parsed && parsed.dismissed);
};

export const dismissResendGuidance = userId => {
  if (!userId) {
    return;
  }

  writePersistedJson(resendGuidanceDismissedKey(userId), {dismissed: true});
};

export const clearResendGuidanceDismissed = userId => {
  if (!userId) {
    return;
  }

  removePersistedKey(resendGuidanceDismissedKey(userId));
};

export const clearAllVerifyAttemptState = userId => {
  if (!userId) {
    return;
  }

  clearUserVerifyAttemptCounters(userId);
  clearVerifyLockout(userId);
  clearResendCooldown(userId);
  clearResendTracking(userId);
  clearResendGuidanceDismissed(userId);
};

export const resetStaleVerifyStateAfterExpiredLockout = userId => {
  if (!userId) {
    return;
  }

  if (!getVerifyLockout(userId) && isVerifyAttemptLimitActive(userId)) {
    clearVerifyAttemptLimitActive(userId);
    clearHitFiveAttemptFlag(userId);
    clearUserVerifyAttemptCounters(userId);
  }
};

export const resetVerifyAttemptsAfterLockoutExpired = userId => {
  if (!userId) {
    return;
  }

  clearVerifyLockout(userId);
  clearUserVerifyAttemptCounters(userId);
};

export const saveVerifyLockout = (
  userId,
  retryAfterSeconds = TWO_FACTOR_VERIFY_LOCKOUT_DEFAULT_SEC,
  message = TWO_FACTOR_VERIFY_ERRORS.LOCKOUT,
) => {
  if (!userId || !retryAfterSeconds) {
    return;
  }

  writePersistedJson(lockoutKey(userId), {
    expiresAt: Date.now() + retryAfterSeconds * 1000,
    message,
  });
};

export const getVerifyLockout = userId => {
  if (!userId) {
    return null;
  }

  const parsed = readPersistedJson(lockoutKey(userId));
  if (!parsed || !parsed.expiresAt) {
    return null;
  }

  const retryAfterSeconds = Math.ceil((parsed.expiresAt - Date.now()) / 1000);
  if (retryAfterSeconds <= 0) {
    removePersistedKey(lockoutKey(userId));
    return null;
  }

  return {
    message: parsed.message || TWO_FACTOR_VERIFY_ERRORS.LOCKOUT,
    retryAfterSeconds,
  };
};

export const clearVerifyLockout = userId => {
  if (!userId) {
    return;
  }

  removePersistedKey(lockoutKey(userId));
};

export const isVerifyLockedOut = userId => {
  resetStaleVerifyStateAfterExpiredLockout(userId);

  return (
    isVerifyAttemptLimitActive(userId) || getVerifyLockout(userId) != null
  );
};

export const saveResendCooldown = (userId, seconds) => {
  if (!userId || !seconds) {
    return;
  }

  writePersistedJson(resendCooldownKey(userId), {
    expiresAt: Date.now() + seconds * 1000,
  });
};

export const saveResendCooldownFromSeconds = (userId, seconds) => {
  if (!userId || !seconds) {
    return 0;
  }

  saveResendCooldown(userId, seconds);
  return seconds;
};

export const getResendCooldownRemaining = userId => {
  if (!userId) {
    return 0;
  }

  const parsed = readPersistedJson(resendCooldownKey(userId));
  if (!parsed || !parsed.expiresAt) {
    return 0;
  }

  const remaining = Math.ceil((parsed.expiresAt - Date.now()) / 1000);
  if (remaining <= 0) {
    removePersistedKey(resendCooldownKey(userId));
    return 0;
  }

  return remaining;
};

export const clearResendCooldown = userId => {
  if (!userId) {
    return;
  }

  removePersistedKey(resendCooldownKey(userId));
};

export const startResendCooldown = userId => {
  const seconds = getHitFiveAttemptFlag(userId)
    ? RESEND_COOLDOWN_AFTER_FIVE_ATTEMPTS_SEC
    : RESEND_COOLDOWN_NORMAL_SEC;

  saveResendCooldown(userId, seconds);
  return seconds;
};

export const getResendCooldownDuration = userId =>
  getHitFiveAttemptFlag(userId)
    ? RESEND_COOLDOWN_AFTER_FIVE_ATTEMPTS_SEC
    : RESEND_COOLDOWN_NORMAL_SEC;



// WEBPACK FOOTER //
// ./src/helpers/twoFactorVerifyAttempts.js